import { ComponentType, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { startOfDay } from 'date-fns';
import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { AnimatedCheckbox } from '@components/checkbox';
import { InlineDatePicker } from '@components/datepicker';
import { ConditionComparator, FormFieldValue, isNegativeComparator } from '@components/form/v2';
import { IconProps } from '@components/icons';
import { IconContains } from '@components/icons/contains';
import { IconContainsNot } from '@components/icons/contains-not';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconDirectionUp } from '@components/icons/direction-up';
import { IconEquals } from '@components/icons/equals';
import { IconEqualsNot } from '@components/icons/equals-not';
import { IconMappingOff } from '@components/icons/mapping-off';
import { useListOptions } from '@components/integrations/lists';
import { Menu } from '@components/menu';
import { AnimatedRadio } from '@components/radio-group/animated-radio';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { FieldMappingFieldItem, FieldType } from '@components/text-editors/addons/field-mapping';
import {
  FIELD_MAPPING_ICON_COLOR,
  FIELD_MAPPING_ICON_SIZE,
  fieldTypeIconMap,
} from '@components/text-editors/addons/field-mapping-type-maps';
import { isVariableSourceMissing } from '@components/text-editors/addons/field-mapping-variable-label';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { isValidDateString } from '@utils/validators';

import { hasValue, isMultiComparator, normalizeVariableOptions } from './condition-helpers';

const DATE_RANGE_COMPARATORS: ReadonlySet<ConditionComparator> = new Set(['before', 'after']);

export const comparatorColor = (comparator: ConditionComparator): Color => {
  if (DATE_RANGE_COMPARATORS.has(comparator)) return 'elementOrange200';
  return isNegativeComparator(comparator) ? 'elementRed200' : 'elementGreen200';
};

const getIconForSelection = (isSelected: boolean, isMultiSelector: boolean) => {
  if (isSelected) {
    return isMultiSelector ? <AnimatedCheckbox checked /> : <AnimatedRadio checked />;
  }
  return isMultiSelector ? <AnimatedCheckbox checked={false} /> : <AnimatedRadio checked={false} />;
};

export const ValueMenu = ({
  options,
  comparator,
  value,
  onSelect,
}: {
  options: Array<SelectOption>;
  comparator: ConditionComparator;
  value?: FormFieldValue;
  onSelect(value: FormFieldValue): void;
}) => {
  const isMulti = isMultiComparator(comparator);

  const isSelected = (optionValue: string) => {
    return value === optionValue || (Array.isArray(value) && value.includes(optionValue));
  };

  return (
    <>
      {options.map((option, index) => {
        return (
          <Menu.Item
            icon={getIconForSelection(isSelected(option.value), isMulti)}
            key={`${option.value}_${index}`}
            label={option.label}
            selectionType="open"
            onSelect={() => onSelect(option.value)}
          />
        );
      })}
    </>
  );
};

export type FormatVariableValueLabels = {
  placeholder: string;
  yes: string;
  no: string;
};

// Inline-then-list resolution order matches the condition runtime so the picker shows
// the same choices the runtime evaluates against.
export const useVariableOptions = (variable: FieldMappingFieldItem): SelectOption[] => {
  const listOptions = useListOptions(variable.optionsFromListId, variable.excludedOptions);
  const inline = useMemo(() => normalizeVariableOptions(variable), [variable]);
  return inline.length > 0 ? inline : listOptions;
};

// Options are passed in (not derived from `variable.options`) so list-backed values resolve to
// their label rather than their raw id.
export const formatVariableValue = (
  variable: FieldMappingFieldItem,
  value: FormFieldValue | undefined,
  options: SelectOption[],
  labels: FormatVariableValueLabels,
): string => {
  if (value === undefined || !hasValue(value)) return labels.placeholder;

  if (variable.type === 'boolean') {
    return value ? labels.yes : labels.no;
  }

  const lookupLabel = (entry: string | number | boolean): string => {
    if (options.length === 0) return String(entry);
    const option = options.find((candidate) => candidate.value === entry);
    return String(option?.label ?? entry);
  };

  const labelled = Array.isArray(value)
    ? value.map((entry) => lookupLabel(entry)).join(', ')
    : lookupLabel(value);

  if (variable.type === 'date' && isValidDateString(labelled)) {
    return abbreviateDate(labelled, 'en', 'date-fancy');
  }
  return labelled;
};

// The backend resolves decimal values through normalize_decimal (trailing zeros stripped, integral
// values kept whole). Canonicalize the typed input the same way so the stored value reads
// identically to what it will match (e.g. "1.50" → "1.5"). Non-numeric input is left as-is.
export const normalizeDecimalValue = (raw: string): string => {
  const parsed = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(parsed)) return raw;
  return String(parsed);
};

// A typed value is stored in the form the backend produces for its type: number → a JS number,
// decimal → a normalize_decimal'd string (see normalizeDecimalValue), anything text-like → the raw
// string. The condition runtime compares numerics by value either way; storing the canonical form
// keeps the saved condition readable and round-trippable. Centralized and pure so the per-type
// contract can be tested in isolation.
export const toStoredTextValue = (
  variableType: FieldType | undefined,
  raw: string,
): FormFieldValue => {
  if (variableType === 'number') {
    const parsed = Number(raw);
    return raw.trim() === '' || !Number.isFinite(parsed) ? raw : parsed;
  }
  if (variableType === 'decimal') return normalizeDecimalValue(raw);
  return raw;
};

export const comparatorIconMap: Record<ConditionComparator, ComponentType<IconProps>> = {
  is: IconEquals,
  not_is: IconEqualsNot,
  starts: IconContains,
  not_starts: IconContainsNot,
  contains: IconContains,
  not_contains: IconContainsNot,
  any_of: IconContains,
  none_of: IconContainsNot,
  before: IconDirectionUp,
  on: IconEquals,
  after: IconDirectionDown,
};

export const comparatorGroupMap: Record<ConditionComparator, string> = {
  is: 'equality',
  not_is: 'equality',
  on: 'date',
  starts: 'text',
  not_starts: 'text',
  contains: 'text',
  not_contains: 'text',
  any_of: 'any',
  none_of: 'any',
  before: 'date',
  after: 'date',
};

export const VariableIcon = ({ variable }: { variable: FieldMappingFieldItem }) => {
  if (isVariableSourceMissing(variable)) {
    return <IconMappingOff size={FIELD_MAPPING_ICON_SIZE} color="elementRed100" />;
  }
  const Icon = fieldTypeIconMap[variable.type ?? 'text'] ?? fieldTypeIconMap.text;
  return <Icon size={FIELD_MAPPING_ICON_SIZE} color={FIELD_MAPPING_ICON_COLOR} />;
};

// HeadlessUI Menu has a document-level keydown listener for type-ahead navigation. Without
// stopImmediatePropagation in the bubble phase, typing inside a TextField rendered in a Menu
// panel closes the menu after the first keystroke.
export const KeyboardIsolated = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const stop = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return;
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    node.addEventListener('keydown', stop);
    node.addEventListener('keyup', stop);
    node.addEventListener('keypress', stop);
    return () => {
      node.removeEventListener('keydown', stop);
      node.removeEventListener('keyup', stop);
      node.removeEventListener('keypress', stop);
    };
  }, []);
  return <div ref={ref}>{children}</div>;
};

type ComparatorMenuItemsProps = {
  comparators: ReadonlyArray<ConditionComparator>;
  onSelect(comparator: ConditionComparator): void;
};

export const ComparatorMenuItems = ({ comparators, onSelect }: ComparatorMenuItemsProps) => {
  const { t } = useScopedTranslate();
  return (
    <>
      {comparators.map((comparator, index) => {
        const Icon = comparatorIconMap[comparator];
        const prev = index > 0 ? comparators[index - 1] : null;
        const shouldSeparate =
          prev !== null && comparatorGroupMap[prev] !== comparatorGroupMap[comparator];
        return (
          <Fragment key={comparator}>
            {shouldSeparate && <Menu.Separator />}
            <Menu.Item
              // Filter-UI parity: menu entries read "Is", "Starts with" —
              // the trailing "..." in the translations belongs to contexts
              // that continue into a value prompt.
              label={t(`core.comparator.${comparator}`).replace('...', '')}
              icon={<Icon />}
              selectionType="open"
              onSelect={() => onSelect(comparator)}
            />
          </Fragment>
        );
      })}
    </>
  );
};

type VariableTextValueInputProps = {
  label: string;
  initialValue: string;
  type: 'text' | 'number';
  onCommit(value: string): void;
  onSubmit?(): void;
};

// Buffers locally because the row re-renders on each save — a controlled input would lose
// focus mid-type and let the Menu dismiss. Commits on blur and on unmount.
export const VariableTextValueInput = ({
  label,
  initialValue,
  type,
  onCommit,
  onSubmit,
}: VariableTextValueInputProps) => {
  const { t } = useScopedTranslate();
  const [draft, setDraft] = useState(initialValue);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const lastCommittedRef = useRef(initialValue);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const flush = () => {
    if (draftRef.current !== lastCommittedRef.current) {
      lastCommittedRef.current = draftRef.current;
      commitRef.current(draftRef.current);
    }
  };

  useEffect(() => {
    return () => flush();
  }, []);

  return (
    <Stack space={2}>
      <TextField
        label={label}
        hideLabel
        value={draft}
        onChange={setDraft}
        onBlur={flush}
        type={type}
        stopPropagation
      />
      {onSubmit && (
        <ButtonGroup align="right">
          <Button
            variant="primary"
            text={t('core.save')}
            isDisabled={draft.trim() === ''}
            onClick={() => {
              flush();
              onSubmit();
            }}
          />
        </ButtonGroup>
      )}
    </Stack>
  );
};

type VariableValueMenuProps = {
  variable: FieldMappingFieldItem;
  comparator: ConditionComparator;
  value: FormFieldValue | undefined;
  onChange(next: FormFieldValue): void;
  onClose?(): void;
};

export const VariableValueMenu = ({
  variable,
  comparator,
  value,
  onChange,
  onClose,
}: VariableValueMenuProps) => {
  const { t } = useScopedTranslate();
  const variableType: FieldType | undefined = variable.type;
  const variableOptions = useVariableOptions(variable);
  const isMulti = isMultiComparator(comparator);
  const isOptionsType =
    variableType === 'select' || variableType === 'multi_select' || variableType === 'options';

  if (variableType === 'boolean') {
    // ValueMenu speaks strings, but the stored value stays a real boolean so formatVariableValue
    // and the condition runtime compare against true/false, not the strings "true"/"false".
    return (
      <ValueMenu
        options={[
          { value: 'true', label: t('core.yes') },
          { value: 'false', label: t('core.no') },
        ]}
        comparator={comparator}
        value={value === true ? 'true' : value === false ? 'false' : undefined}
        onSelect={(nextValue) => {
          onChange(nextValue === 'true');
          onClose?.();
        }}
      />
    );
  }

  // Guard on the type, not on whether options have loaded: a list-backed set that hasn't resolved
  // yet must still show the options menu, not fall back to a free-text box that accepts any value.
  if (isOptionsType) {
    return (
      <>
        <ValueMenu
          options={variableOptions}
          comparator={comparator}
          value={value}
          onSelect={(nextValue) => {
            onChange(nextValue);
            if (!isMulti) onClose?.();
          }}
        />
        {/* Single-select closes on pick; multi-select keeps toggling, so it needs an explicit
            commit to dismiss — without one the checkbox list strands the user with no way out. */}
        {isMulti && onClose && (
          <>
            <Menu.Separator />
            <VariableInputContainer>
              <ButtonGroup align="right">
                <Button
                  variant="primary"
                  text={t('core.save')}
                  isDisabled={!hasValue(value)}
                  onClick={onClose}
                />
              </ButtonGroup>
            </VariableInputContainer>
          </>
        )}
      </>
    );
  }

  if (variableType === 'date') {
    return (
      <KeyboardIsolated>
        <VariableInputContainer>
          <InlineDatePicker
            value={typeof value === 'string' && value ? value : null}
            onChange={(next) => {
              onChange(startOfDay(next).toISOString());
              onClose?.();
            }}
          />
        </VariableInputContainer>
      </KeyboardIsolated>
    );
  }

  const isNumberInput = variableType === 'number' || variableType === 'decimal';
  return (
    <KeyboardIsolated>
      <VariableInputContainer>
        <VariableTextValueInput
          label={variable.label}
          initialValue={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
          type={isNumberInput ? 'number' : 'text'}
          onCommit={(raw) => onChange(toStoredTextValue(variableType, raw))}
          onSubmit={onClose}
        />
      </VariableInputContainer>
    </KeyboardIsolated>
  );
};

const VariableInputContainer = styled.div({
  padding: pxToRem(8),
});
